import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { CronScheduleEditor } from "@/components/cron/CronScheduleEditor.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { useCallback } from "react";

type CronScheduleComponentData = Extract<ArtifactComponent, { type: "cronSchedule" }>;

/**
 * The artifact view's schedule block: the shared {@link CronScheduleEditor}
 * writing each edit straight back to the artifact through the view's patcher.
 *
 * Saves per edit rather than on a Save button because that is the patcher's
 * contract; the canvas routine card overrides the patcher to buffer instead, so
 * a live routine still commits explicitly.
 */
export const CronScheduleBlock = ({ component }: { component: CronScheduleComponentData }) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;

  const save = useCallback(
    async (next: string) => {
      try {
        await patchOne(component.editPath, next);
      } catch (error) {
        console.error(error);
        errorToast("Error updating schedule");
      }
    },
    [patchOne, component.editPath],
  );

  return (
    <CronScheduleEditor
      value={component.value}
      onChange={(next) => void save(next)}
      disabled={!canEdit}
      timezone={component.timezone}
    />
  );
};
